import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';

import { createSentryEventThrottle } from './sentry-event-throttle';

/**
 * Throttling for noisy runtime errors (e.g. "Cannot read properties of
 * undefined") before they reach Sentry. A broken render can fire the same error
 * dozens of times per second across many users, so we report each distinct
 * error signature at most once per cooldown window, with a global per-session
 * cap as a hard safety net.
 */

/** Same error signature is reported at most once within this window. */
const SIGNATURE_COOLDOWN_MS = 5 * 60 * 1000;
/** Hard cap on the number of runtime errors reported per page session. */
const MAX_EVENTS_PER_SESSION = 50;

const runtimeErrorThrottle = createSentryEventThrottle({
  cooldownMs: SIGNATURE_COOLDOWN_MS,
  maxPerSession: MAX_EVENTS_PER_SESSION,
});

/**
 * Error messages that are expected noise, not real bugs, and must never reach
 * Sentry — filtered here so they never consume a per-session event slot or a
 * signature cooldown (the counter increments before `captureException`, so
 * Sentry's own `ignoreErrors` in `Sentry.init` would drop the event too late).
 *
 * SignalR WebSocket transport (transient / self-healing):
 * - "Handshake was canceled": an in-flight hub handshake aborted by
 *   page/navigation teardown (socket closes before the handshake completes) —
 *   fires across every hub at once on unload and is harmless.
 * - "WebSocket failed to connect": an automatic-reconnect attempt failing after
 *   an abnormal socket close (1006), common on mobile/WKWebView network drops —
 *   SignalR keeps retrying on its own, so a single failed attempt is not
 *   actionable.
 * - "Server timeout elapsed": SignalR's keep-alive timeout when no message
 *   (including server pings) arrives within `serverTimeoutInMilliseconds` —
 *   typical of backgrounded tabs / flaky networks; triggers auto-reconnect.
 *
 * In-app browser native bridge (mirrors `Sentry.init` ignoreErrors — Sentry
 * THE-NEW-M7). The Instagram/Facebook Android IAB injects a telemetry script
 * that calls native Java objects; a late flush after the WebView is torn down
 * throws "Java object is gone". These usually arrive via `window.onerror`
 * (already dropped by `Sentry.init`), but are filtered here too in case one
 * reaches the Angular ErrorHandler path.
 *
 * The iOS counterpart is "window.webkit.messageHandlers": an in-app browser
 * (the Google app's, for one) injects a user-script that replaces a native API
 * — `window.print` in Sentry THE-NEW 7719496753 — with a bridge to the host
 * app. Where the matching message handler is not registered,
 * `window.webkit.messageHandlers.<name>` is undefined and our perfectly normal
 * call throws. Matched on the expression WebKit puts in the message, so it
 * covers any handler name, and deliberately narrower than a `user-script`
 * stack-frame filter: a real bug of ours can pass through an injected frame.
 */
const IGNORED_ERROR_MESSAGES = [
  'Handshake was canceled',
  'WebSocket failed to connect',
  'Server timeout elapsed',
  'Java object is gone',
  'enableDidUserTypeOnKeyboardLogging',
  'window.webkit.messageHandlers',
];

/** True when the error is known expected noise that should not be reported. */
function isIgnoredError(error: unknown): boolean {
  if (!error) return false;

  let message = '';
  let stack = '';
  if (error instanceof Error) {
    message = error.message;
    stack = error.stack ?? '';
  } else if (typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  }

  if (IGNORED_ERROR_MESSAGES.some((ignored) => message.includes(ignored))) {
    return true;
  }

  // Native-bridge scripts injected by in-app browsers (Instagram/Facebook
  // `iabjs://`) are never our code — mirrors the `Sentry.init` denyUrls filter.
  return stack.includes('iabjs://');
}
/**
 * Builds a stable key for an error so repeats collapse while distinct bugs stay
 * apart: error name + message + the first stack frame (the throw site).
 */
function errorSignature(error: unknown): string {
  if (error instanceof Error) {
    const firstFrame = error.stack?.split('\n')[1]?.trim() ?? '';
    return `${error.name}:${error.message}:${firstFrame}`;
  }
  return String(error);
}

/**
 * Reports a runtime error to Sentry, throttled by signature and capped per
 * session. HTTP errors are ignored here — they are reported with full request
 * context by the HTTP interceptor. Returns `true` when the error was sent.
 */
export function reportRuntimeErrorToSentry(error: unknown): boolean {
  // Avoid double-reporting: HTTP failures are handled by the HTTP interceptor.
  if (error instanceof HttpErrorResponse) return false;

  // Expected teardown noise (e.g. cancelled SignalR handshakes) never reports.
  if (isIgnoredError(error)) return false;

  if (!runtimeErrorThrottle.shouldReport(errorSignature(error))) return false;

  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'runtime');
    Sentry.captureException(error);
  });
  return true;
}
